import { useCallback } from 'react';
import {
  DropzoneOptions,
  ErrorCode,
  FileError,
  FileRejection,
} from 'react-dropzone';
import {
  DOCUMENTS_DROPZONE_ACCEPTED_FILES,
  DOCUMENTS_DROPZONE_MAX_SIZE_BYTES,
  TICKETS_ATTACHMENTS_DROPZONE_ACCEPTED_FILES,
  TICKET_ATTACHMENTS_DROPZONE_MAX_SIZE_BYTES,
  DOCUMENTS_MAX_SIZE_GB,
} from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { FileItem, FileStatus } from '@sib/types';
import { v4 as uuidv4 } from 'uuid';

export const DROPZONE_CONFIG: Partial<DropzoneOptions> = {
  accept: DOCUMENTS_DROPZONE_ACCEPTED_FILES,
  maxSize: DOCUMENTS_DROPZONE_MAX_SIZE_BYTES,
};

export const TICKET_DROPZONE_CONFIG: Partial<DropzoneOptions> = {
  accept: TICKETS_ATTACHMENTS_DROPZONE_ACCEPTED_FILES,
  maxSize: TICKET_ATTACHMENTS_DROPZONE_MAX_SIZE_BYTES,
};

export const getFileCounts = (files: FileItem[]) => {
  const validFilesCount = files.filter(
    ({ status }) => status === FileStatus.Pending
  ).length;

  return `${validFilesCount}/${files.length}`;
};
interface UseOnDropHandlerProps {
  addFiles: (files: FileItem[]) => void;
  maxSize?: string;
}

export const useOnDropHandler = ({
  addFiles,
  maxSize = `${DOCUMENTS_MAX_SIZE_GB} GB`,
}: UseOnDropHandlerProps) => {
  const { t } = useTranslation();

  const getErrorMessage = useCallback(
    (errors: readonly FileError[]) => {
      if (errors.some(err => err.code === ErrorCode.FileTooLarge))
        return t('fileManager.sizeExceedsLimit', {
          limit: maxSize,
        });

      return t('fileManager.invalidFormat');
    },
    [t, maxSize]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newAcceptedFiles = acceptedFiles.map(file => ({
        id: uuidv4(),
        file,
        selected: false,
        status: FileStatus.Pending,
        progress: 0,
      }));

      const newRejectedFiles = fileRejections.map(({ file, errors }) => ({
        id: uuidv4(),
        file,
        selected: false,
        status: FileStatus.Error,
        errorMessage: getErrorMessage(errors),
        progress: 0,
      }));

      addFiles([...newAcceptedFiles, ...newRejectedFiles]);
    },
    [addFiles, getErrorMessage]
  );

  return { onDrop };
};

export const downloadFileFromUrl = (url: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = url;
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadPublicFile = (fileName: string) => {
  const url = `${window.location.origin}/${fileName}`;

  downloadFileFromUrl(url, fileName);
};

export const downloadBlobAsFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);

  downloadFileFromUrl(url, fileName);

  URL.revokeObjectURL(url);
};

export const getFileNameWithoutExtension = (filename: string) =>
  filename.split('.').slice(0, -1).join('.') || filename;

export const getFileExtension = (filename: string) =>
  filename.split('.').pop()?.toLowerCase() || '';

export const isPdfFile = (filename: string) =>
  getFileExtension(filename) === 'pdf';

export const isOfficeModernFile = (filename: string) => {
  const ext = getFileExtension(filename);

  return [
    'xlsx',
    'xlsm',
    'xlsb',
    'docx',
    'docm',
    'pptx',
    'ppsx',
    'odp',
  ].includes(ext);
};

export const isOfficeLegacyFile = (filename: string) => {
  const ext = getFileExtension(filename);

  return ['doc', 'ppt', 'pps', 'pot'].includes(ext);
};

export const isImageFile = (filename: string) => {
  const extension = getFileExtension(filename);

  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
    extension
  );
};

export const isUnsupportedFormat = (filename?: string) => {
  if (!filename) return false;

  const extension = getFileExtension(filename);

  return ['xls'].includes(extension);
};

export const isFilePreviewSupported = (filename: string) =>
  isPdfFile(filename) ||
  isImageFile(filename) ||
  isOfficeModernFile(filename) ||
  isOfficeLegacyFile(filename);
